import API_BASE, { protectedApiFetch } from "../api";
import { isCanonicalPushInstallationId } from "./pushInstallation";
import { isPushRegistrationEnabled } from "./pushRegistrationConfig";

export const PUSH_TEST_NOTIFICATION_UI_ENV =
  "REACT_APP_PUSH_TEST_NOTIFICATION_UI_ENABLED";

const STABLE_ERROR_CODES = new Set([
  "test_notification_unavailable",
  "test_notification_retry_later",
  "test_notification_invalid",
]);
const ATTEMPT_STATES = new Set([
  "pending",
  "sent",
  "confirmed",
  "failed",
  "expired",
]);

export class PushTestNotificationApiError extends Error {
  constructor(code, status = null) {
    super(code);
    this.name = "PushTestNotificationApiError";
    this.code = code;
    this.status = status;
  }
}

export function isPushTestNotificationUiEnabled(environment = process.env) {
  return (
    String(environment?.[PUSH_TEST_NOTIFICATION_UI_ENV] || "")
      .trim()
      .toLowerCase() === "true" && isPushRegistrationEnabled(environment)
  );
}

function unavailable(status = null) {
  return new PushTestNotificationApiError(
    "test_notification_unavailable",
    status
  );
}

async function request(path, options) {
  let response;
  try {
    response = await protectedApiFetch(`${API_BASE}${path}`, options);
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    throw unavailable();
  }
  if (!response.ok) {
    let detail = "";
    try {
      const payload = await response.json();
      detail = typeof payload?.detail === "string" ? payload.detail : "";
    } catch {
      detail = "";
    }
    throw new PushTestNotificationApiError(
      STABLE_ERROR_CODES.has(detail)
        ? detail
        : "test_notification_unavailable",
      response.status
    );
  }
  try {
    return await response.json();
  } catch {
    throw unavailable(response.status);
  }
}

function validTimestamp(value) {
  return typeof value === "string" && !Number.isNaN(Date.parse(value));
}

function sanitizeAttempt(payload, allowedStates = ATTEMPT_STATES) {
  if (
    !payload ||
    !allowedStates.has(payload.status) ||
    !isCanonicalPushInstallationId(payload.attemptId) ||
    !validTimestamp(payload.expiresAt) ||
    (payload.confirmedAt !== null &&
      payload.confirmedAt !== undefined &&
      !validTimestamp(payload.confirmedAt))
  ) {
    throw unavailable();
  }
  return {
    status: payload.status,
    attemptId: payload.attemptId.toLowerCase(),
    expiresAt: payload.expiresAt,
    confirmedAt: payload.confirmedAt || null,
  };
}

function validateDeviceReference(currentDeviceReference) {
  if (!isCanonicalPushInstallationId(currentDeviceReference)) {
    throw unavailable();
  }
  return currentDeviceReference.toLowerCase();
}

export async function fetchPushTestNotificationAvailability({ signal } = {}) {
  const payload = await request("/notifications/test-push/status", {
    method: "GET",
    signal,
  });
  if (!payload || typeof payload.available !== "boolean") throw unavailable();
  return payload.available;
}

export async function sendPushTestNotification(
  currentDeviceReference,
  { keepalive = false, signal } = {}
) {
  const reference = validateDeviceReference(currentDeviceReference);
  const payload = await request("/notifications/test-push", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ currentDeviceReference: reference }),
    keepalive,
    signal,
  });
  return sanitizeAttempt(payload, new Set(["sent"]));
}

export async function fetchPushTestAttempt(
  attemptId,
  currentDeviceReference,
  { signal } = {}
) {
  const normalizedAttemptId = validateDeviceReference(attemptId);
  const reference = validateDeviceReference(currentDeviceReference);
  const payload = await request(
    `/notifications/test-push/${encodeURIComponent(
      normalizedAttemptId
    )}?currentDeviceReference=${encodeURIComponent(reference)}`,
    { method: "GET", signal }
  );
  return sanitizeAttempt(payload);
}

export async function confirmPushTestAttempt(
  attemptId,
  currentDeviceReference,
  { signal } = {}
) {
  const normalizedAttemptId = validateDeviceReference(attemptId);
  const reference = validateDeviceReference(currentDeviceReference);
  const payload = await request(
    `/notifications/test-push/${encodeURIComponent(
      normalizedAttemptId
    )}/confirm`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ currentDeviceReference: reference }),
      signal,
    }
  );
  return sanitizeAttempt(payload);
}
